// (C) Copyright 2025 Hewlett Packard Enterprise Development LP
import React, { useCallback, useEffect, useState } from 'react'
import { Box, FormField } from 'grommet'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'
import { useFlags } from 'launchdarkly-react-client-sdk'

import {
  Button,
  CCSForm,
  FormInput,
  ModalDialog,
  ModalHeader,
  Notification,
  Dropdown,
  Typography
} from '../../../../components'
import { get, post } from '../../../../utils/api-utils'
import Markdown from '../../../../commoncomponents/Markdown'
import { displayApiError } from '../../../../utils/error-handling-utils'
import TextInputWithCopyButton from '../../../manage-account/components/TextInputWithCopyButton/TextInputWithCopyButton'
import TextAreaInputWithCopyButton from '../../../manage-account/components/TextAreaInputWithCopyButton/TextAreaInputWithCopyButton'

const SidePanelBox = styled(Box)`
  max-width: 396px;
`
const StyledBox = styled(Box)`
  max-width: 528px;
`

const CreateScimIntegration = ({ onSetOpen, onSuccess }) => {
  const { t } = useTranslation(['common', 'iam', 'manage', 'authn'])
  const LDFlags = useFlags()
  const scimAPIEndpoint = LDFlags['glcp-scim-api-endpoints']
  const [errorMessage, setErrorMessage] = useState('')
  const [formValues, setFormValues] = useState({})
  const [domains, setDomains] = useState([])
  const [isFormSubmitted, setIsFormSubmitted] = useState(false)
  const [credentialData, setCredentialData] = useState([])
  // search filter
  const [filteredDomains, setFilteredDomains] = useState([])
  const [hasCopied, setHasCopied] = useState(false)
  const [validationMSG, setValidationMSG] = useState(false)
  const APIEndpoint = `${scimAPIEndpoint}/identity/v2alpha1/scim/v2`

  const navigate = useNavigate()

  const getDomains = useCallback(async () => {
    try {
      const response = await get(
        '/internal-platform-tenant-ui/v2alpha2/domains'
      )
      setDomains(response?.data?.items)
      setFilteredDomains(response?.data?.items)
    } catch (error) {
      setErrorMessage(displayApiError(error, t, setErrorMessage))
    }
  }, [t])

  const handleCheckCopied = () => {
    setHasCopied(true)
    setValidationMSG(false)
  }

  const handleSubmit = () => {
    const domainName = formValues?.domain?.name
    const reqBody = {
      name: formValues?.name,
      domains: [domainName]
    }
    post('/internal-identity/v2alpha1/scim-integrations', reqBody)
      .then((res) => {
        if (res?.data) {
          setIsFormSubmitted(true)
          post(
            `/internal-identity/v2alpha1/scim-integrations/${res?.data?.id}/scim-credentials`,
            { credentialType: 'SCIM_KEY' }
          )
            .then((response) => {
              setCredentialData(response?.data)
            })
            .catch((err) =>
              setErrorMessage(displayApiError(err, t, setErrorMessage))
            )
        }
      })
      .catch((error) => {
        setErrorMessage(displayApiError(error, t, setErrorMessage))
      })
  }

  useEffect(() => {
    getDomains()
  }, [getDomains])

  const getScimSubheader = () => {
    if (isFormSubmitted) {
      return (
        <Box data-testid="scim-integration-created-subtitle" gap="small">
          <Typography type="text" testId="subtitle">
            <Markdown>
              {t('iam:external_scim_integrations.access_token_subtitle', {
                scimName: formValues?.name
              })}
            </Markdown>
          </Typography>
          <Typography type="text" testId="subtitle">
            {t('iam:external_scim_integrations.access_token_subtitle_2')}
          </Typography>
        </Box>
      )
    }
    return (
      <Box testId="create-scim-integration-subtitle" gap="xsmall">
        <Typography type="text" testId="subtitle">
          {t('iam:external_scim_integrations.create_scim_subtitle')}
        </Typography>
        <Typography type="text" testId="subtitle">
          {t('iam:external_scim_integrations.create_scim_subtitle_2')}
        </Typography>
        <Typography type="text" testId="subtitle">
          {t('iam:external_scim_integrations.create_scim_subtitle_3')}
        </Typography>
      </Box>
    )
  }

  return (
    <>
      {errorMessage}
      <ModalDialog
        position={isFormSubmitted ? 'center' : 'right'}
        overflow="hidden"
        header={
          <ModalHeader
            title={
              <Box pad={{ bottom: 'medium' }}>
                <Typography
                  type="heading"
                  level="2"
                  testId="create-scim-integration-title"
                >
                  {isFormSubmitted
                    ? t(
                        'iam:external_scim_integrations.scim_integration_created_title'
                      )
                    : t(
                        'iam:external_scim_integrations.create_scim_integration_title'
                      )}
                </Typography>
              </Box>
            }
            onClose={() => {
              onSetOpen(false)
            }}
          />
        }
        content={
          <CCSForm
            value={formValues}
            onChange={setFormValues}
            errorMessage=""
            testId="create-scim-integrations-form"
            validate="blur"
            onSubmit={handleSubmit}
            buttons={
              !isFormSubmitted ? (
                <Box
                  direction="row"
                  justify="start"
                  gap="small"
                  pad={{ top: 'medium' }}
                >
                  <Button
                    label={t('iam:external_scim_integrations.create_scim_btn')}
                    primary
                    type="submit"
                    testId="create-scim-integration-modal-btn"
                  />
                  <Button
                    label={t('common:cancel')}
                    onClick={() => {
                      onSetOpen(false)
                    }}
                    testId="cancel-button"
                  />
                </Box>
              ) : (
                <Box
                  justify="end"
                  align="end"
                  pad={{ top: 'medium' }}
                  width="100%"
                >
                  <Button
                    label={t('common:close')}
                    onClick={() => {
                      if (!hasCopied && !validationMSG) {
                        setValidationMSG(true)
                      } else {
                        onSetOpen(false)
                        if (!onSuccess) {
                          navigate('/manage-account/organization/external-scim')
                        } else {
                          onSuccess()
                        }
                      }
                    }}
                    primary
                    testId="close-btn"
                  />
                </Box>
              )
            }
          >
            {isFormSubmitted ? (
              <StyledBox>
                <Box gap="medium">
                  {getScimSubheader()}
                  <Box gap="xsmall">
                    <TextAreaInputWithCopyButton
                      value={credentialData?.scimKey}
                      label={t(
                        'iam:external_scim_integrations.scim_client_secret'
                      )}
                      testId="scim-access-token"
                      copyTextCallback={handleCheckCopied}
                    />
                    <TextInputWithCopyButton
                      value={APIEndpoint}
                      label={t(
                        'iam:external_scim_integrations.scim_api_endpoint'
                      )}
                      testId="scim-api-endpoint-form-field"
                      pad
                    />
                    {validationMSG && (
                      <Notification
                        type="inline"
                        backgroundColor="status-warning"
                        status="warning"
                        pad={{ horizontal: 'small', vertical: 'xsmall' }}
                        text={
                          <Typography type="text" testId="scim-warning-note2">
                            {t(
                              'iam:external_scim_integrations.scim_warning_note2'
                            )}
                          </Typography>
                        }
                        testId="scim-integration-waring-notification"
                      />
                    )}
                  </Box>
                </Box>
              </StyledBox>
            ) : (
              <SidePanelBox>
                {getScimSubheader()}
                <Box pad={{ right: 'xsmall' }}>
                  <Box>
                    <FormInput
                      labelHelper={t(
                        'iam:external_scim_integrations.name_label_help'
                      )}
                      value={formValues.name}
                      label={t('name')}
                      name="name"
                      onChange={(event) => {
                        event.stopPropagation()
                        setFormValues({
                          ...formValues,
                          name: event?.target?.value
                        })
                      }}
                      inputType="text"
                      required
                      testId="create-scim-integration-name"
                    />
                  </Box>
                  <Box
                    data-testid="create-scim-integration-domains-dropdown"
                    margin={{ vertical: 'small' }}
                  >
                    <Box>
                      <FormField
                        label={t('iam:sso_profiles.column_domains')}
                        help={t(
                          'iam:external_scim_integrations.domain_label_help'
                        )}
                        name="domain"
                        required
                        data-testid="create-scim-integration-domains-dropdown-field"
                      >
                        <Dropdown
                          name="domain"
                          options={filteredDomains}
                          labelKey="name"
                          value={formValues.domain}
                          searchPlaceholder={t('search')}
                          valueKey="name"
                          placeholder={t('select')}
                          onChange={({ value }) => {
                            setFormValues({
                              ...formValues,
                              domain: value
                            })
                          }}
                          onSearch={(text) => {
                            const regexp = new RegExp(text, 'i')
                            setFilteredDomains(
                              domains.filter((o) => regexp.test(o?.name))
                            )
                          }}
                          onClose={() => {
                            setFilteredDomains(domains)
                          }}
                          testId="create-scim-integration-domains-dropdown-field-btn"
                        />
                      </FormField>
                    </Box>
                  </Box>
                  <Box gap="small" pad={{ vertical: 'xsmall' }}>
                    <Notification
                      status="info"
                      type="inline"
                      text={t(
                        'iam:external_scim_integrations.scim_on_domains_info'
                      )}
                    />
                    <Notification
                      type="inline"
                      pad="xsmall"
                      backgroundColor="status-warning"
                      status="warning"
                      text={
                        <Typography type="text" testId="scim-warning-note">
                          {t(
                            'iam:external_scim_integrations.scim_warning_note'
                          )}
                        </Typography>
                      }
                      testId="scim-integration-waring-notification"
                    />
                    <Notification
                      status="info"
                      type="inline"
                      text={t('iam:external_scim_integrations.scim_help_info')}
                    />
                  </Box>
                </Box>
              </SidePanelBox>
            )}
          </CCSForm>
        }
        onClose={() => onSetOpen(false)}
        testId="create-scim-integration-modal-dialog"
      />
    </>
  )
}
CreateScimIntegration.propTypes = {
  onSetOpen: PropTypes.func.isRequired,
  onSuccess: PropTypes.func
}

export default CreateScimIntegration
